/**
 * Product Quiz Selector
 * Guides customers through 3 questions to recommend the perfect hair care product
 */

(function() {
  'use strict';

  // Selectors
  const SELECTORS = {
    section: '.js-product-quiz-selector',
    question: '.js-quiz-question',
    answer: '.js-quiz-answer',
    back: '.js-quiz-back',
    progressSection: '.js-quiz-progress',
    progressFill: '.js-quiz-progress-fill',
    progressText: '.js-quiz-progress-text',
    results: '.js-quiz-results',
    product: '.js-quiz-product',
    addToCart: '.js-quiz-add-to-cart',
    retake: '.js-quiz-retake'
  };

  // CSS Classes
  const CLASSES = {
    active: 'is-active',
    hidden: 'is-hidden',
    loading: 'is-loading'
  };

  // Configuration
  const CONFIG = {
    storageKey: 'product_quiz_results',
    animationDuration: 300, // milliseconds
    totalQuestions: 0 // Will be set dynamically on init
  };

  // Product mapping based on concern + care level
  const PRODUCT_MAP = {
    'dryness-minimal': 'triple-action-strength-moisture-balm',
    'dryness-full': 'the-gems-collection-cleanse-condition-nourish-seal',
    'breakage-minimal': 'restore-repair-deep-conditioner-restore-repair',
    'breakage-full': 'the-gems-collection-cleanse-condition-nourish-seal',
    'scalp-minimal': 'the-revive-set-cleanse-restore',
    'scalp-full': 'the-gems-collection-cleanse-condition-nourish-seal',
    'growth-minimal': 'the-growth-set-nourish-and-strengthen',
    'growth-full': 'the-gems-collection-cleanse-condition-nourish-seal'
  };

  // State
  let state = {
    currentQuestion: 1,
    answers: [], // Array of answers indexed by question number (0-based)
    recommendedProduct: null,
    productData: null,
    sectionElement: null
  };

  /**
   * Initialize the quiz
   */
  function init() {
    state.sectionElement = document.querySelector(SELECTORS.section);
    if (!state.sectionElement) return;

    // Detect total questions dynamically
    const questions = state.sectionElement.querySelectorAll(SELECTORS.question);
    CONFIG.totalQuestions = questions.length;

    // Don't auto-load from LocalStorage in Shopify theme editor
    const isThemeEditor = window.Shopify && window.Shopify.designMode;

    if (!isThemeEditor) {
      checkLocalStorage();
    }

    attachEventListeners();
  }

  /**
   * Attach all event listeners
   */
  function attachEventListeners() {
    // Answer button clicks
    const answerButtons = state.sectionElement.querySelectorAll(SELECTORS.answer);
    answerButtons.forEach(function(button) {
      button.addEventListener('click', handleAnswerClick);
    });

    // Back button clicks
    const backButtons = state.sectionElement.querySelectorAll(SELECTORS.back);
    backButtons.forEach(function(button) {
      button.addEventListener('click', handleBackClick);
    });

    // Add to cart button
    const addToCartButton = state.sectionElement.querySelector(SELECTORS.addToCart);
    if (addToCartButton) {
      addToCartButton.addEventListener('click', handleAddToCart);
    }

    // Retake button
    const retakeButton = state.sectionElement.querySelector(SELECTORS.retake);
    if (retakeButton) {
      retakeButton.addEventListener('click', handleRetake);
    }
  }

  /**
   * Handle answer button click
   */
  function handleAnswerClick(e) {
    const button = e.currentTarget;
    const questionNum = parseInt(button.getAttribute('data-question'));
    const answerValue = button.getAttribute('data-value');

    // Store the answer
    storeAnswer(questionNum, answerValue);

    // Update aria-checked for accessibility
    const questionContainer = button.closest(SELECTORS.question);
    const allAnswers = questionContainer.querySelectorAll(SELECTORS.answer);
    allAnswers.forEach(function(btn) {
      btn.setAttribute('aria-checked', 'false');
    });
    button.setAttribute('aria-checked', 'true');

    // Proceed to next question or show results
    if (questionNum < CONFIG.totalQuestions) {
      setTimeout(function() {
        transitionToQuestion(questionNum + 1);
      }, CONFIG.animationDuration);
    } else {
      setTimeout(function() {
        showResults();
      }, CONFIG.animationDuration);
    }
  }

  /**
   * Handle back button click
   */
  function handleBackClick(e) {
    const button = e.currentTarget;
    const currentQuestion = button.closest(SELECTORS.question);
    const questionNum = parseInt(currentQuestion.getAttribute('data-question'));

    if (questionNum > 1) {
      transitionToQuestion(questionNum - 1);
    }
  }

  /**
   * Store answer in state
   */
  function storeAnswer(questionNum, value) {
    state.answers[questionNum - 1] = value;
  }

  /**
   * Transition to a specific question
   */
  function transitionToQuestion(targetQuestionNum) {
    const questions = state.sectionElement.querySelectorAll(SELECTORS.question);

    // First, fade out current question
    questions.forEach(function(q) {
      const qNum = parseInt(q.getAttribute('data-question'));
      if (qNum !== targetQuestionNum) {
        q.classList.remove(CLASSES.active);
        setTimeout(function() {
          q.classList.add(CLASSES.hidden);
        }, CONFIG.animationDuration);
      }
    });

    // Then, after fade out completes, fade in target question
    setTimeout(function() {
      questions.forEach(function(q) {
        const qNum = parseInt(q.getAttribute('data-question'));
        if (qNum === targetQuestionNum) {
          q.classList.remove(CLASSES.hidden);
          q.classList.add(CLASSES.active);

          // Focus first answer for accessibility
          setTimeout(function() {
            const firstAnswer = q.querySelector(SELECTORS.answer);
            if (firstAnswer) firstAnswer.focus();
          }, CONFIG.animationDuration);
        }
      });
    }, CONFIG.animationDuration);

    // Update progress indicator
    state.currentQuestion = targetQuestionNum;
    updateProgress();
  }

  /**
   * Update progress indicator
   */
  function updateProgress() {
    const progressText = state.sectionElement.querySelector(SELECTORS.progressText);
    const progressFill = state.sectionElement.querySelector(SELECTORS.progressFill);

    if (progressText) {
      progressText.textContent = state.currentQuestion + ' of ' + CONFIG.totalQuestions;
    }

    if (progressFill) {
      // Progress bar starts empty and fills one-third at a time
      // Question 1 = 0%, Question 2 = 33.33%, Question 3 = 66.66%
      const percentage = ((state.currentQuestion - 1) / CONFIG.totalQuestions) * 100;
      progressFill.style.width = percentage + '%';
    }
  }

  /**
   * Show results after completing all questions
   */
  function showResults() {
    // Animate progress bar to 100% before hiding
    const progressFill = state.sectionElement.querySelector(SELECTORS.progressFill);
    if (progressFill) {
      progressFill.style.width = '100%';
    }

    // Wait for animation to complete before hiding progress and showing results
    setTimeout(function() {
      // Hide all questions
      const questions = state.sectionElement.querySelectorAll(SELECTORS.question);
      questions.forEach(function(q) {
        q.classList.remove(CLASSES.active);
        q.classList.add(CLASSES.hidden);
      });

      // Hide progress section
      const progressSection = state.sectionElement.querySelector(SELECTORS.progressSection);
      if (progressSection) {
        progressSection.classList.add(CLASSES.hidden);
      }

      // Add modifier class to remove padding from questions wrapper
      const questionsWrapper = state.sectionElement.querySelector('.product-quiz-selector__questions-wrapper');
      if (questionsWrapper) {
        questionsWrapper.classList.add('showing-results');
      }

      // Get recommended product
      const productHandle = getRecommendedProductHandle();
      state.recommendedProduct = productHandle;

      // Fetch and display product
      fetchProduct(productHandle)
        .then(function(product) {
          state.productData = product;
          renderProduct(product);
          saveToLocalStorage();

          // Show results container
          const resultsContainer = state.sectionElement.querySelector(SELECTORS.results);
          if (resultsContainer) {
            resultsContainer.classList.remove(CLASSES.hidden);
            resultsContainer.classList.add(CLASSES.active);
          }
        })
        .catch(function(error) {
          console.error('Error fetching product:', error);
          renderError();
        });
    }, CONFIG.animationDuration);
  }

  /**
   * Get recommended product handle based on answers
   * Assumes Q2 is concern (index 1) and Q3 is care level (index 2)
   */
  function getRecommendedProductHandle() {
    const concern = state.answers[1]; // Q2
    const careLevel = state.answers[2]; // Q3

    const key = concern + '-' + careLevel;
    return PRODUCT_MAP[key] || 'the-gems-collection-cleanse-condition-nourish-seal';
  }

  /**
   * Fetch product data from Shopify
   */
  function fetchProduct(handle) {
    return fetch('/products/' + handle + '.js')
      .then(function(response) {
        if (!response.ok) {
          throw new Error('Product not found');
        }
        return response.json();
      });
  }

  /**
   * Get custom image for a product handle from data attributes
   */
  function getCustomImage(productHandle) {
    const section = state.sectionElement;
    const imageMap = {
      'triple-action-strength-moisture-balm': section.getAttribute('data-image-moisture-balm'),
      'restore-repair-deep-conditioner-restore-repair': section.getAttribute('data-image-restore-repair'),
      'the-revive-set-cleanse-restore': section.getAttribute('data-image-revive-set'),
      'the-growth-set-nourish-and-strengthen': section.getAttribute('data-image-growth-set'),
      'the-gems-collection-cleanse-condition-nourish-seal': section.getAttribute('data-image-complete-experience')
    };

    return imageMap[productHandle] || null;
  }

  /**
   * Get custom description for a product handle from data attributes
   */
  function getCustomDescription(productHandle) {
    const section = state.sectionElement;
    const descriptionMap = {
      'triple-action-strength-moisture-balm': section.getAttribute('data-description-moisture-balm'),
      'restore-repair-deep-conditioner-restore-repair': section.getAttribute('data-description-restore-repair'),
      'the-revive-set-cleanse-restore': section.getAttribute('data-description-revive-set'),
      'the-growth-set-nourish-and-strengthen': section.getAttribute('data-description-growth-set'),
      'the-gems-collection-cleanse-condition-nourish-seal': section.getAttribute('data-description-complete-experience')
    };

    return descriptionMap[productHandle] || null;
  }

  /**
   * Render product in results area
   */
  function renderProduct(product) {
    const productContainer = state.sectionElement.querySelector(SELECTORS.product);
    if (!productContainer) return;

    const variant = product.variants[0];
    const price = formatMoney(variant.price);

    console.log('%c===========Product==============\n\n', 'color: green; font-weight: bold;', product);

    // Use custom image if provided, otherwise use product image
    const customImage = getCustomImage(product.handle);
    const image = customImage || product.featured_image || '';

    // Use custom description if provided, otherwise use product description
    const customDescription = getCustomDescription(product.handle);
    const description = customDescription || product.description || '';

    const html =
      '<div class="product-quiz-result">' +
        '<div class="product-quiz-result__image-wrapper">' +
          '<img src="' + image + '" alt="' + escapeHtml(product.title) + '" class="product-quiz-result__image" loading="lazy">' +
        '</div>' +
        '<div class="product-quiz-result__details">' +
          '<h3 class="product-quiz-selector__result-heading">Your Hair Ritual Match</h3>' +
          '<a style="text-decoration: none;" href="' + escapeHtml(product.url) + '"><h4 class="product-quiz-result__title">' + escapeHtml(product.title) + '</h4></a>' +
          '<p class="product-quiz-result__price">' + price + '</p>' +
          (description ? '<div class="product-quiz-result__description">' + description + '</div>' : '') +
          '<div class="product-quiz-selector__actions">' +
            '<button class="product-quiz-selector__add-to-cart button button--primary js-quiz-add-to-cart" type="button">Add to Cart</button>' +
            '<button class="product-quiz-selector__retake js-quiz-retake" type="button">Retake Quiz</button>' +
          '</div>' +
        '</div>' +
      '</div>';

    productContainer.innerHTML = html;

    // Re-attach event listeners to the newly created buttons
    const addToCartButton = state.sectionElement.querySelector(SELECTORS.addToCart);
    if (addToCartButton) {
      addToCartButton.addEventListener('click', handleAddToCart);
    }

    const retakeButton = state.sectionElement.querySelector(SELECTORS.retake);
    if (retakeButton) {
      retakeButton.addEventListener('click', handleRetake);
    }
  }

  /**
   * Render error message
   */
  function renderError() {
    const productContainer = state.sectionElement.querySelector(SELECTORS.product);
    if (!productContainer) return;

    productContainer.innerHTML =
      '<div class="product-quiz-result product-quiz-result--error">' +
        '<p>We\'re sorry, but we couldn\'t find the perfect product for you at this time. Please try again or browse our catalog.</p>' +
      '</div>';

    // Hide add to cart button
    const addToCartButton = state.sectionElement.querySelector(SELECTORS.addToCart);
    if (addToCartButton) {
      addToCartButton.style.display = 'none';
    }

    // Show results container
    const resultsContainer = state.sectionElement.querySelector(SELECTORS.results);
    if (resultsContainer) {
      resultsContainer.classList.remove(CLASSES.hidden);
      resultsContainer.classList.add(CLASSES.active);
    }
  }

  /**
   * Handle add to cart button click
   */
  function handleAddToCart(e) {
    const button = e.currentTarget;

    if (!state.productData || !state.productData.variants || !state.productData.variants[0]) {
      return;
    }

    const variantId = state.productData.variants[0].id;

    button.classList.add(CLASSES.loading);
    button.disabled = true;

    addProductToCart(variantId)
      .then(function() {
        button.classList.remove(CLASSES.loading);
        button.disabled = false;

        // Trigger cart notification if available
        if (window.themeCore && window.themeCore.CartNotification) {
          window.themeCore.CartNotification.refresh();
        } else {
          // Fallback: trigger cart drawer or notification
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        }

        announceToScreenReader('Product added to cart');
      })
      .catch(function(error) {
        console.error('Error adding to cart:', error);
        button.classList.remove(CLASSES.loading);
        button.disabled = false;
        alert('Sorry, we couldn\'t add this product to your cart. Please try again.');
      });
  }

  /**
   * Add product to cart via Shopify API
   */
  function addProductToCart(variantId) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        id: variantId,
        quantity: 1
      })
    })
    .then(function(response) {
      if (!response.ok) {
        throw new Error('Failed to add to cart');
      }
      return response.json();
    });
  }

  /**
   * Handle retake button click
   */
  function handleRetake() {
    // Clear state
    state.currentQuestion = 1;
    state.answers = [];
    state.recommendedProduct = null;
    state.productData = null;

    // Clear LocalStorage
    clearLocalStorage();

    // Reset all aria-checked attributes
    const allAnswers = state.sectionElement.querySelectorAll(SELECTORS.answer);
    allAnswers.forEach(function(btn) {
      btn.setAttribute('aria-checked', 'false');
    });

    // Hide results
    const resultsContainer = state.sectionElement.querySelector(SELECTORS.results);
    if (resultsContainer) {
      resultsContainer.classList.remove(CLASSES.active);
      resultsContainer.classList.add(CLASSES.hidden);
    }

    // Show progress section
    const progressSection = state.sectionElement.querySelector(SELECTORS.progressSection);
    if (progressSection) {
      progressSection.classList.remove(CLASSES.hidden);
    }

    // Remove modifier class to restore padding on questions wrapper
    const questionsWrapper = state.sectionElement.querySelector('.product-quiz-selector__questions-wrapper');
    if (questionsWrapper) {
      questionsWrapper.classList.remove('showing-results');
    }

    // Show first question
    transitionToQuestion(1);

    announceToScreenReader('Quiz reset. Starting from question 1');
  }

  /**
   * Check LocalStorage for saved quiz results
   */
  function checkLocalStorage() {
    if (!supportsLocalStorage()) return;

    try {
      const saved = localStorage.getItem(CONFIG.storageKey);
      if (saved) {
        const data = JSON.parse(saved);

        // Validate data
        if (data.completed && data.answers && data.productHandle) {
          // Restore state
          state.answers = data.answers;
          state.recommendedProduct = data.productHandle;

          // Show results directly
          fetchProduct(data.productHandle)
            .then(function(product) {
              state.productData = product;
              renderProduct(product);

              // Hide questions and progress
              const questions = state.sectionElement.querySelectorAll(SELECTORS.question);
              questions.forEach(function(q) {
                q.classList.add(CLASSES.hidden);
                q.classList.remove(CLASSES.active);
              });

              const progressSection = state.sectionElement.querySelector(SELECTORS.progressSection);
              if (progressSection) {
                progressSection.classList.add(CLASSES.hidden);
              }

              // Add modifier class to remove padding from questions wrapper
              const questionsWrapper = state.sectionElement.querySelector('.product-quiz-selector__questions-wrapper');
              if (questionsWrapper) {
                questionsWrapper.classList.add('showing-results');
              }

              // Show results
              const resultsContainer = state.sectionElement.querySelector(SELECTORS.results);
              if (resultsContainer) {
                resultsContainer.classList.remove(CLASSES.hidden);
                resultsContainer.classList.add(CLASSES.active);
              }
            })
            .catch(function(error) {
              console.error('Error loading saved results:', error);
              clearLocalStorage();
            });
        }
      }
    } catch (e) {
      console.error('Error reading LocalStorage:', e);
      clearLocalStorage();
    }
  }

  /**
   * Save quiz results to LocalStorage
   */
  function saveToLocalStorage() {
    if (!supportsLocalStorage()) return;

    try {
      const data = {
        timestamp: Date.now(),
        answers: state.answers,
        productHandle: state.recommendedProduct,
        completed: true
      };
      localStorage.setItem(CONFIG.storageKey, JSON.stringify(data));
    } catch (e) {
      console.error('Error saving to LocalStorage:', e);
    }
  }

  /**
   * Clear LocalStorage
   */
  function clearLocalStorage() {
    if (!supportsLocalStorage()) return;

    try {
      localStorage.removeItem(CONFIG.storageKey);
    } catch (e) {
      console.error('Error clearing LocalStorage:', e);
    }
  }

  /**
   * Check if LocalStorage is available
   */
  function supportsLocalStorage() {
    try {
      const test = '__localStorage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch (e) {
      return false;
    }
  }

  /**
   * Format money using Shopify's format or fallback
   */
  function formatMoney(pence) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(pence);
    }
    // Fallback: simple formatting
    const dollars = (pence / 100).toFixed(2);
    return '£' + dollars;
  }

  /**
   * Escape HTML to prevent XSS
   */
  function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Announce to screen readers
   */
  function announceToScreenReader(message) {
    const announcement = document.createElement('div');
    announcement.setAttribute('role', 'status');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'visually-hidden';
    announcement.textContent = message;
    document.body.appendChild(announcement);

    setTimeout(function() {
      document.body.removeChild(announcement);
    }, 1000);
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
